/**
 * Route Map Component
 * Map showing routes with polylines and stop markers
 * Matches Preview page implementation exactly
 */

import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { cn } from '../../utils/cn';
import type { Route, RouteStop } from '../../types';
import type { RouteStopWithStatus } from '../../services/routes.api';
import { BAGHDAD_WAREHOUSE } from '../tasks/warehouse';
import { setMapLanguageToArabic, ARABIC_MAP_STYLE } from '../../utils/mapbox';
import { isValidCoordinate } from '../../utils/coordinates';
import { ROUTE_COLORS } from './routeColors';
import { escapeHtml } from '../../utils/escapeHtml';

interface RouteMapProps {
  routes: Route[];
  stops: Map<string, RouteStop[]>;
  selectedRouteId?: string;
  selectedStopId?: string;
  visibleRouteIds?: Set<string>;
  onRouteSelect?: (routeId: string) => void;
  onStopSelect?: (stop: RouteStop) => void;
  className?: string;
  centerOnLocation?: { lat: number; lng: number };
  routeStatusMap?: Map<string, { stops: RouteStopWithStatus[] }>;
}

const DEFAULT_CENTER: [number, number] = [BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat];
const DEFAULT_ZOOM = 11;

export default function RouteMap({
  routes,
  stops,
  selectedRouteId,
  selectedStopId,
  visibleRouteIds,
  onStopSelect,
  className,
  centerOnLocation,
  routeStatusMap,
}: RouteMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: ARABIC_MAP_STYLE,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Set Arabic language for map labels
    setMapLanguageToArabic(map.current);

    map.current.on('load', () => {
      setMapLoaded(true);
    });

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Handle centering on a specific location
  useEffect(() => {
    if (!map.current || !mapLoaded || !centerOnLocation) return;
    
    map.current.flyTo({
      center: [centerOnLocation.lng, centerOnLocation.lat],
      zoom: 16,
      duration: 1000,
    });
  }, [centerOnLocation, mapLoaded]);


  // Clear map layers and markers
  const clearMapLayers = () => {
    if (!map.current) return;

    // Remove markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Remove route layers and sources
    const style = map.current.getStyle();
    if (style?.layers) {
      style.layers.forEach(layer => {
        if (layer.id.startsWith('route-')) {
          map.current?.removeLayer(layer.id);
        }
      });
    }
    if (style?.sources) {
      Object.keys(style.sources).forEach(sourceId => {
        if (sourceId.startsWith('route-')) {
          map.current?.removeSource(sourceId);
        }
      });
    }
  };

  // Add warehouse marker (same as Preview page)
  const addWarehouseMarker = () => {
    if (!map.current) return;

    const el = document.createElement('div');
    el.innerHTML = `
      <div style="
        width: 40px;
        height: 40px;
        background: #3B82F6;
        border: 3px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        cursor: pointer;
      ">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
          <path d="M4 21V9L12 3L20 9V21H14V14H10V21H4Z"/>
        </svg>
      </div>
    `;

    const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
      <div style="padding: 8px;">
        <strong>Baghdad Sorting Center</strong><br/>
        <span style="color: #666; font-size: 12px;">Start/End Location</span>
      </div>
    `);

    const marker = new mapboxgl.Marker({ element: el })
      .setLngLat([BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat])
      .setPopup(popup)
      .addTo(map.current);

    markersRef.current.push(marker);
  };

  // Draw a single route with polyline and markers
  const drawRoute = (route: Route, routeIndex: number) => {
    if (!map.current) return;

    const routeStops = stops.get(route._id) || [];
    if (routeStops.length === 0) return;

    const color = route.route_color || ROUTE_COLORS[routeIndex % ROUTE_COLORS.length];
    const isSelected = route._id === selectedRouteId;
    const isHubStart = route.start_location_mode === 'hub_start';

    // Build coordinates based on hub_start setting, filtering invalid coordinates
    // If hub_start: warehouse -> stops -> warehouse
    // If free_start: stops -> warehouse (drivers start from their zone)
    const stopCoordinates = routeStops
      .filter(stop => isValidCoordinate(stop.lat, stop.lng))
      .map(stop => [stop.lng, stop.lat] as [number, number]);
    const coordinates: [number, number][] = isHubStart
      ? [
          [BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat], // Start from warehouse
          ...stopCoordinates,
          [BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat], // Return to warehouse
        ]
      : [
          ...stopCoordinates,
          [BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat], // Only return to warehouse
        ];

    // Add route line
    const sourceId = `route-line-${route._id}`;
    const layerId = `route-layer-${route._id}`;

    if (!map.current.getSource(sourceId)) {
      map.current.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates,
          },
        },
      });

      map.current.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': color,
          'line-width': isSelected ? 4.5 : 3,
          'line-opacity': isSelected ? 1 : 0.7,
        },
      });
    }

    // Add stop markers (same as Preview page), skipping invalid coordinates
    routeStops.forEach((stop, stopIndex) => {
      if (!isValidCoordinate(stop.lat, stop.lng)) {
        console.warn(`Skipping stop with invalid coordinates: ${stop.task_uid}`, { lat: stop.lat, lng: stop.lng });
        return;
      }

      const isStopSelected = stop._id === selectedStopId;
      const isRTO = stop.type === 'rto';
      const isCash = stop.type === 'cash';
      const hasTimeWindow = false; // TODO: Add time window support if available

      // Check if this stop is completed
      const stopsWithStatus = routeStatusMap?.get(route._id)?.stops;
      const stopStatus = stopsWithStatus?.find(s => s.task_uid === stop.task_uid)?.task_status;
      const isDone = stopStatus === 'done';

      const el = document.createElement('div');
      el.innerHTML = `
        <div style="position: relative;">
          <div style="
            width: ${isStopSelected ? 32 : 26}px;
            height: ${isStopSelected ? 32 : 26}px;
            background: ${escapeHtml(color)};
            border: 2px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: ${isStopSelected ? 14 : 12}px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            cursor: pointer;
          ">${isDone ? `
            <svg width="${isStopSelected ? 18 : 14}" height="${isStopSelected ? 18 : 14}" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          ` : escapeHtml(stopIndex + 1)}</div>
          ${isRTO ? `
            <div style="
              position: absolute;
              top: -4px;
              right: -4px;
              width: 14px;
              height: 14px;
              background: #F59E0B;
              border: 1.5px solid white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 1px 3px rgba(0,0,0,0.3);
            ">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="white">
                <path d="M19 7v4H5.83l3.58-3.59L8 6l-6 6 6 6 1.41-1.41L5.83 13H21V7h-2z"/>
              </svg>
            </div>
          ` : isCash ? `
            <div style="
              position: absolute;
              top: -4px;
              right: -4px;
              width: 14px;
              height: 14px;
              background: #D97706;
              border: 1.5px solid white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 1px 3px rgba(0,0,0,0.3);
              font-size: 8px;
              color: white;
              font-weight: bold;
            ">$</div>
          ` : ''}
          ${hasTimeWindow ? `
            <div style="
              position: absolute;
              top: -4px;
              left: -4px;
              width: 14px;
              height: 14px;
              background: #3B82F6;
              border: 1.5px solid white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 1px 3px rgba(0,0,0,0.3);
            ">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7V7z"/>
              </svg>
            </div>
          ` : ''}
        </div>
      `;

      el.addEventListener('click', () => {
        onStopSelect?.(stop);
      });

      const taskTypeBadge = isRTO 
        ? '<span style="background: #F59E0B; color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;">RTO</span>'
        : '<span style="background: #3B82F6; color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;">Pickup</span>';

      const statusBadge = isDone 
        ? '<span style="background: #10B981; color: white; padding: 3px 8px; border-radius: 4px; font-size: 11px; font-weight: 600;">✓ Done</span>'
        : stopStatus === 'in-progress'
        ? '<span style="background: #F59E0B; color: white; padding: 3px 8px; border-radius: 4px; font-size: 11px; font-weight: 600;">In Progress</span>'
        : stopStatus === 'arrived'
        ? '<span style="background: #3B82F6; color: white; padding: 3px 8px; border-radius: 4px; font-size: 11px; font-weight: 600;">Arrived</span>'
        : '';

      const popup = new mapboxgl.Popup({ offset: 25, closeButton: false, maxWidth: '280px' }).setHTML(`
        <div style="padding: 14px; font-family: system-ui, -apple-system, sans-serif;">
          <div style="display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px;">
            <div style="
              width: 28px;
              height: 28px;
              background: ${escapeHtml(color)};
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: 14px;
              flex-shrink: 0;
            ">${escapeHtml(stopIndex + 1)}</div>
            <div style="flex: 1; min-width: 0;">
              <div style="font-weight: 600; font-size: 15px; color: #111; margin-bottom: 4px; line-height: 1.3;">
                ${escapeHtml(stop.merchant_name || 'Unknown Location')}
              </div>
              <div style="display: flex; gap: 6px; flex-wrap: wrap;">
                ${taskTypeBadge}
                ${statusBadge}
              </div>
            </div>
          </div>
          <div style="font-size: 13px; color: #666; margin-bottom: 12px; line-height: 1.4;">
            ${escapeHtml(stop.address || 'Address not available')}
          </div>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; font-size: 13px;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="font-size: 16px;">📦</span>
              <div>
                <div style="font-weight: 600; color: #111;">${escapeHtml(stop.parcel_count || 0)}</div>
                <div style="font-size: 11px; color: #999;">parcels</div>
              </div>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="font-size: 16px;">🕐</span>
              <div>
                <div style="font-weight: 600; color: #111;">${escapeHtml(stop.eta ? new Date(stop.eta).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Baghdad' }) : '--:--')}</div>
                <div style="font-size: 11px; color: #999;">arrival</div>
              </div>
            </div>
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="font-size: 16px;">⏱️</span>
              <div>
                <div style="font-weight: 600; color: #111;">${escapeHtml(stop.service_time_min || 0)}m</div>
                <div style="font-size: 11px; color: #999;">service</div>
              </div>
            </div>
          </div>
        </div>
      `);

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([stop.lng, stop.lat])
        .setPopup(popup)
        .addTo(map.current!);

      markersRef.current.push(marker);
    });
  };

  // Fit map bounds to show all stops
  const fitMapBounds = () => {
    if (!map.current || routes.length === 0) return;

    const bounds = new mapboxgl.LngLatBounds();
    bounds.extend([BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat]);

    routes.forEach(route => {
      const routeStops = stops.get(route._id) || [];
      routeStops.forEach(stop => {
        if (isValidCoordinate(stop.lat, stop.lng)) {
          bounds.extend([stop.lng, stop.lat]);
        }
      });
    });

    if (!bounds.isEmpty()) {
      map.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 14,
      });
    }
  };

  // Draw routes on map when data changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Clear existing layers and markers
    clearMapLayers();

    // Add warehouse marker first
    addWarehouseMarker();

    // Draw routes with straight lines
    // Use original index for consistent colors regardless of visibility
    routes.forEach((route, routeIndex) => {
      // Skip routes that are hidden (if visibleRouteIds is provided)
      if (visibleRouteIds && !visibleRouteIds.has(route._id)) {
        return;
      }
      // Pass the original routeIndex to ensure consistent color assignment
      drawRoute(route, routeIndex);
    });

    // Fit bounds
    fitMapBounds();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes, stops, selectedRouteId, selectedStopId, visibleRouteIds, mapLoaded, routeStatusMap]);

  // Show placeholder if no Mapbox token
  if (!mapboxgl.accessToken) {
    return (
      <div className={cn('bg-gray-100 flex items-center justify-center h-full w-full', className)}>
        <div className="text-center text-gray-500">
          <p className="font-medium">Map not available</p>
          <p className="text-sm">Set VITE_MAPBOX_TOKEN to enable map view</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={mapContainer}
      className={cn('h-full w-full', className)}
    />
  );
}
